import { createSlice } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

export const DIM_LAYER_GROUPS = [
  'overall',
  'upper',
  'lower',
  'detail',
  'accessory',
];
const allLayers = visible =>
  Object.fromEntries(DIM_LAYER_GROUPS.map(g => [g, visible]));

const initialState = {
  // Bật/tắt lớp DIM overlay trên viewport
  enabled: false,
  // Danh sách DIM (xem createDimension trong three/core/dimension)
  items: [],
  selectedId: null,
  // DIM đang mở bảng thuộc tính / menu chuột phải (chỉ là state UI, không lưu)
  editingId: null,
  contextMenu: null, // { id, x, y }
  // Chế độ thao tác: 'idle' | 'manual' (click 2 điểm tạo DIM) | 'annotate'
  // (click 1 điểm tạo ghi chú)
  mode: 'idle',
  // Lớp hiển thị theo nhóm và preset đang chọn ('all' | tên nhóm | 'custom')
  layers: allLayers(true),
  preset: 'all',
  // Góc nhìn đã lưu: [{id, name, position:[x,y,z], target:[x,y,z],
  //   dim?: {layers, preset, hiddenIds}}] — `dim` là bộ DIM nào được hiện
  views: [],
  manual: {
    // 'auto' | 'width' | 'height' | 'depth' | 'aligned'
    axis: 'auto',
    // true: sau mỗi DIM, điểm cuối thành điểm đầu của DIM kế tiếp
    chain: false,
    // Số điểm đã chọn dở dang (0 hoặc 1), để toolbar hiện hướng dẫn
    pendingCount: 0,
    // Số đoạn đã tạo trong chuỗi đang đo (0 khi không có chuỗi đang dở)
    chainCount: 0,
    // Lệnh từ nút trên toolbar gửi cho controller: { name: 'finish' | 'undo', nonce }
    command: null,
  },
  // Đơn vị hiển thị: 'mm' | 'm' | 'in'
  unit: 'mm',
};

const exitManual = state => {
  state.mode = 'idle';
  state.manual.pendingCount = 0;
  state.manual.chainCount = 0;
};

export const dimensionReducer = createSlice({
  name: 'Dimension',
  initialState,
  reducers: {
    setDimensionEnabled: (state, action) => {
      state.enabled = !!action.payload;
      if (!state.enabled) exitManual(state);
    },
    toggleDimensionEnabled: state => {
      state.enabled = !state.enabled;
      if (!state.enabled) exitManual(state);
    },
    setDimensionMode: (state, action) => {
      state.mode = ['manual', 'annotate'].includes(action.payload)
        ? action.payload
        : 'idle';
      state.manual.pendingCount = 0;
      state.manual.chainCount = 0;
      if (state.mode !== 'idle') {
        // Tránh panel/menu che khi đang đo
        state.editingId = null;
        state.contextMenu = null;
      }
    },
    setLayerVisible: (state, action) => {
      const { group, visible } = action.payload || {};
      if (!DIM_LAYER_GROUPS.includes(group)) return;
      state.layers[group] = !!visible;
      state.preset = 'custom';
    },
    /** 'all' hiện mọi nhóm; tên nhóm chỉ hiện nhóm đó (vd. chỉ "Tủ trên"). */
    setPreset: (state, action) => {
      const preset = action.payload;
      if (preset === 'all') {
        state.layers = allLayers(true);
      } else if (DIM_LAYER_GROUPS.includes(preset)) {
        state.layers = allLayers(false);
        state.layers[preset] = true;
      } else {
        return;
      }
      state.preset = preset;
    },
    addView: {
      reducer: (state, action) => {
        state.views.push(action.payload);
      },
      prepare: ({ name, position, target, dim }) => ({
        payload: {
          id: `view_${uuidv4()}`,
          name,
          position,
          target,
          ...(dim ? { dim } : {}),
        },
      }),
    },
    /** Ghi đè góc nhìn + bộ DIM đang hiện vào một view đã có (giữ id, tên). */
    updateView: (state, action) => {
      const { id, position, target, dim } = action.payload || {};
      const view = state.views.find(v => v.id === id);
      if (!view) return;
      if (position) view.position = position;
      if (target) view.target = target;
      if (dim) view.dim = dim;
    },
    /**
     * Áp bộ DIM của view: lớp hiển thị, preset và DIM nào bị ẩn. DIM tạo sau khi
     * lưu view (không có trong hiddenIds) được hiện. View cũ không có `dim` thì
     * không đổi gì (chỉ camera).
     */
    applyViewState: (state, action) => {
      const dim = state.views.find(v => v.id === action.payload)?.dim;
      if (!dim) return;
      const layers = allLayers(true);
      DIM_LAYER_GROUPS.forEach(g => {
        if (dim.layers?.[g] === false) layers[g] = false;
      });
      state.layers = layers;
      state.preset = dim.preset ?? 'custom';
      const hidden = new Set(dim.hiddenIds ?? []);
      state.items.forEach(item => {
        item.visible = !hidden.has(item.id);
      });
    },
    removeView: (state, action) => {
      state.views = state.views.filter(v => v.id !== action.payload);
    },
    renameView: (state, action) => {
      const { id, name } = action.payload || {};
      const view = state.views.find(v => v.id === id);
      if (view && typeof name === 'string') view.name = name;
    },
    setDimensionViews: (state, action) => {
      state.views = Array.isArray(action.payload) ? action.payload : [];
    },
    setManualOptions: (state, action) => {
      const { axis, chain } = action.payload || {};
      if (axis !== undefined) state.manual.axis = axis;
      if (chain !== undefined) state.manual.chain = !!chain;
    },
    setManualPendingCount: (state, action) => {
      state.manual.pendingCount = action.payload || 0;
    },
    setManualChainCount: (state, action) => {
      state.manual.chainCount = action.payload || 0;
    },
    /** Nút "Hoàn tất" / "Hoàn tác điểm" trên toolbar: controller thực thi lệnh. */
    requestManualCommand: (state, action) => {
      const name = action.payload;
      if (!['finish', 'undo'].includes(name)) return;
      state.manual.command = {
        name,
        nonce: (state.manual.command?.nonce ?? 0) + 1,
      };
    },
    addDimensions: (state, action) => {
      const items = Array.isArray(action.payload) ? action.payload : [];
      state.items.push(...items);
    },
    setDimensions: (state, action) => {
      state.items = Array.isArray(action.payload) ? action.payload : [];
      state.selectedId = null;
      state.editingId = null;
      state.contextMenu = null;
    },
    /**
     * Thay các DIM tự động cũ của những module liệt kê trong `instanceIds`
     * bằng `items` mới (tránh nhân đôi khi bấm Auto DIM nhiều lần).
     */
    replaceAutoDimensions: (state, action) => {
      const { instanceIds = [], items = [] } = action.payload || {};
      // Chỉ xoá DIM tự động (có autoFor); DIM thủ công / ghi chú không bao giờ bị đụng
      state.items = state.items
        .filter(item => !item.autoFor || !instanceIds.includes(item.autoFor))
        .concat(items);
    },
    updateDimension: (state, action) => {
      const { id, changes } = action.payload || {};
      const target = state.items.find(item => item.id === id);
      if (!target) return;
      const { style, ...rest } = changes || {};
      // Các đoạn của một chuỗi luôn thẳng hàng: đổi khoảng cách một đoạn thì cả
      // chuỗi dời theo cùng một lượng (kéo đường DIM hay sửa ô "khoảng cách")
      if (target.chainId && typeof rest.offsetM === 'number') {
        const delta = rest.offsetM - (target.offsetM ?? 0);
        state.items.forEach(item => {
          if (item.chainId === target.chainId && item.id !== target.id) {
            item.offsetM = (item.offsetM ?? 0) + delta;
          }
        });
      }
      Object.assign(target, rest);
      if (style) target.style = { ...target.style, ...style };
    },
    removeDimension: (state, action) => {
      state.items = state.items.filter(item => item.id !== action.payload);
      if (state.selectedId === action.payload) state.selectedId = null;
      if (state.editingId === action.payload) state.editingId = null;
      state.contextMenu = null;
    },
    /** Xoá cả chuỗi: mọi DIM cùng chainId. */
    removeChain: (state, action) => {
      const chainId = action.payload;
      if (!chainId) return;
      const gone = new Set(
        state.items.filter(i => i.chainId === chainId).map(i => i.id)
      );
      state.items = state.items.filter(i => !gone.has(i.id));
      if (gone.has(state.selectedId)) state.selectedId = null;
      if (gone.has(state.editingId)) state.editingId = null;
      state.contextMenu = null;
    },
    clearDimensions: state => {
      state.items = [];
      state.selectedId = null;
      state.editingId = null;
      state.contextMenu = null;
    },
    selectDimension: (state, action) => {
      state.selectedId = action.payload ?? null;
    },
    openDimensionEditor: (state, action) => {
      state.editingId = action.payload ?? null;
      state.selectedId = action.payload ?? null;
      state.contextMenu = null;
    },
    closeDimensionEditor: state => {
      state.editingId = null;
      // Đóng khung soạn thì DIM hết được chọn: phím Delete trả về cho designer
      state.selectedId = null;
    },
    openDimensionMenu: (state, action) => {
      state.contextMenu = action.payload ?? null;
      state.selectedId = action.payload?.id ?? state.selectedId;
    },
    closeDimensionMenu: state => {
      state.contextMenu = null;
    },
    /**
     * Đánh dấu DIM mất anchor (module đã bị xoá). Chỉ ghi khi tập orphan đổi
     * để không gây re-render mỗi frame.
     */
    setDimensionOrphans: (state, action) => {
      const orphanIds = new Set(action.payload || []);
      state.items.forEach(item => {
        const orphan = orphanIds.has(item.id);
        if (item.orphan !== orphan) item.orphan = orphan;
      });
    },
    // Nhân bản: newId sinh trong prepare để reducer vẫn thuần
    duplicateDimension: {
      reducer: (state, action) => {
        const { id, newId } = action.payload;
        const src = state.items.find(item => item.id === id);
        if (!src) return;
        const copy = JSON.parse(JSON.stringify(src));
        copy.id = newId;
        // Bản sao là DIM thủ công: không bị Auto DIM ghi đè
        delete copy.autoFor;
        if (copy.type === 'annotation') {
          // Ghi chú: dời khung chữ một chút để bản sao không che bản gốc
          const [dx = 0, dy = 0] = copy.labelShift ?? [];
          copy.labelShift = [dx + 18, dy + 18];
        } else {
          copy.offsetM = (copy.offsetM ?? 0) + 0.05;
        }
        state.items.push(copy);
        state.selectedId = newId;
      },
      prepare: id => ({ payload: { id, newId: `dim_${uuidv4()}` } }),
    },
    setDimensionUnit: (state, action) => {
      state.unit = action.payload;
    },
  },
});

export const {
  setDimensionEnabled,
  toggleDimensionEnabled,
  setDimensionMode,
  setLayerVisible,
  setPreset,
  addView,
  updateView,
  applyViewState,
  removeView,
  renameView,
  setDimensionViews,
  setManualOptions,
  setManualPendingCount,
  setManualChainCount,
  requestManualCommand,
  removeChain,
  addDimensions,
  setDimensions,
  replaceAutoDimensions,
  updateDimension,
  removeDimension,
  clearDimensions,
  selectDimension,
  openDimensionEditor,
  closeDimensionEditor,
  openDimensionMenu,
  closeDimensionMenu,
  setDimensionOrphans,
  duplicateDimension,
  setDimensionUnit,
} = dimensionReducer.actions;

export default dimensionReducer.reducer;
